import { useState } from 'react';
import { palette } from '../../styles/tokens';
import { Card } from '../common/Card';
import type { Business, BusinessRules } from '../../types';

interface Props {
  business: Business;
  onSaveRules: (rules: Partial<BusinessRules>) => Promise<boolean>;
  onSaveBusiness: (updates: Partial<Business>) => Promise<boolean>;
}

const ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${palette.g100}` };
const LABEL: React.CSSProperties = { fontSize: 13, color: palette.g500, width: 180, flexShrink: 0 };
const VALUE: React.CSSProperties = { fontSize: 13.5, fontWeight: 500, color: palette.g800, flex: 1 };
const INPUT_NUM: React.CSSProperties = { width: 80, padding: '5px 8px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g800, textAlign: 'right' as const };
const SELECT: React.CSSProperties = { padding: '5px 8px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g800 };

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <div onClick={() => onChange(!on)} style={{
      width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
      background: on ? palette.accent : palette.g300,
      padding: 2, transition: 'background 0.15s', display: 'flex', alignItems: 'center',
    }}>
      <div style={{ width: 16, height: 16, borderRadius: 8, background: palette.white, transition: 'margin-left 0.15s', marginLeft: on ? 16 : 0 }} />
    </div>
  );
}

const PROVINCES: Record<string, string> = { ON: 'Ontario', BC: 'British Columbia', AB: 'Alberta', MB: 'Manitoba', NS: 'Nova Scotia', NL: 'Newfoundland', SK: 'Saskatchewan', QC: 'Quebec' };

export function ComplianceRules({ business, onSaveRules, onSaveBusiness }: Props) {
  const rules = business.business_rules ?? {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    overtime_threshold_hours: rules.overtime_threshold_hours ?? 44,
    rest_period_hours: rules.rest_period_hours ?? 11,
    grace_minutes: rules.grace_minutes ?? 5,
    no_show_timeout: rules.no_show_timeout ?? 15,
    shift_swap_mode: rules.shift_swap_mode ?? 'disabled',
    leave_request_mode: rules.leave_request_mode ?? 'disabled',
    schedule_visibility: rules.schedule_visibility ?? 'own_shifts_only',
    ot_warning_threshold: business.ot_warning_threshold ?? 36,
    require_ot_approval: business.require_ot_approval ?? true,
    require_gps_checkin: business.require_gps_checkin ?? true,
    checkin_radius_m: business.checkin_radius_m ?? 200,
  });

  const startEdit = () => {
    setForm({
      overtime_threshold_hours: rules.overtime_threshold_hours ?? 44,
      rest_period_hours: rules.rest_period_hours ?? 11,
      grace_minutes: rules.grace_minutes ?? 5,
      no_show_timeout: rules.no_show_timeout ?? 15,
      shift_swap_mode: rules.shift_swap_mode ?? 'disabled',
      leave_request_mode: rules.leave_request_mode ?? 'disabled',
      schedule_visibility: rules.schedule_visibility ?? 'own_shifts_only',
      ot_warning_threshold: business.ot_warning_threshold ?? 36,
      require_ot_approval: business.require_ot_approval ?? true,
      require_gps_checkin: business.require_gps_checkin ?? true,
      checkin_radius_m: business.checkin_radius_m ?? 200,
    });
    setEditing(true);
  };

  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async () => {
    setSaveError(null);
    const rulesOk = await onSaveRules({
      overtime_threshold_hours: form.overtime_threshold_hours,
      rest_period_hours: form.rest_period_hours,
      grace_minutes: form.grace_minutes,
      no_show_timeout: form.no_show_timeout,
      shift_swap_mode: form.shift_swap_mode,
      leave_request_mode: form.leave_request_mode,
      schedule_visibility: form.schedule_visibility,
    });
    if (!rulesOk) {
      setSaveError('Failed to save compliance rules. Check your connection and try again.');
      return;
    }
    const bizOk = await onSaveBusiness({
      ot_warning_threshold: form.ot_warning_threshold,
      require_ot_approval: form.require_ot_approval,
      require_gps_checkin: form.require_gps_checkin,
      checkin_radius_m: form.checkin_radius_m,
    } as Partial<Business>);
    if (!bizOk) {
      setSaveError('Rules saved, but business settings failed. Try saving again.');
      return;
    }
    setEditing(false);
  };

  const setN = (key: string, val: string) => setForm(f => ({ ...f, [key]: Number(val) || 0 }));

  if (!editing) {
    const items: [string, string][] = [
      ['Province', `${PROVINCES[business.region_code] ?? business.region_code} (ESA applies)`],
      ['Overtime threshold', `${rules.overtime_threshold_hours ?? 44} hours/week`],
      ['OT warning at', `${business.ot_warning_threshold ?? 36} hours`],
      ['Require OT approval', business.require_ot_approval ? 'Yes' : 'No'],
      ['Rest period', `${rules.rest_period_hours ?? 11} hours between shifts`],
      ['GPS check-in', business.require_gps_checkin ? 'Required' : 'Not required'],
      ['Check-in radius', `${business.checkin_radius_m ?? 200} meters`],
      ['Grace period', `${rules.grace_minutes ?? 5} minutes`],
      ['No-show timeout', `${rules.no_show_timeout ?? 15} minutes`],
      ['Shift swaps', (rules.shift_swap_mode ?? 'disabled').replace(/_/g, ' ')],
      ['Leave requests', (rules.leave_request_mode ?? 'disabled').replace(/_/g, ' ')],
      ['Schedule visibility', (rules.schedule_visibility ?? 'own_shifts_only').replace(/_/g, ' ')],
    ];
    return (
      <Card title="Compliance & Rules" actionLabel="Edit" onAction={startEdit}>
        {items.map(([label, value], i) => (
          <div key={i} style={{ ...ROW, borderBottom: i === items.length - 1 ? 'none' : ROW.borderBottom }}>
            <div style={LABEL}>{label}</div>
            <div style={{ ...VALUE, textTransform: 'capitalize' }}>{value}</div>
          </div>
        ))}
      </Card>
    );
  }

  return (
    <Card title="Compliance & Rules">
      <div style={ROW}><div style={LABEL}>Province</div><div style={VALUE}>{PROVINCES[business.region_code] ?? business.region_code} (read-only)</div></div>
      <div style={ROW}><div style={LABEL}>Overtime threshold</div><div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}><input type="number" style={INPUT_NUM} value={form.overtime_threshold_hours} onChange={e => setN('overtime_threshold_hours', e.target.value)} /><span style={{ fontSize: 12, color: palette.g400 }}>hours/week</span></div></div>
      <div style={ROW}><div style={LABEL}>OT warning at</div><div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}><input type="number" style={INPUT_NUM} value={form.ot_warning_threshold} onChange={e => setN('ot_warning_threshold', e.target.value)} /><span style={{ fontSize: 12, color: palette.g400 }}>hours</span></div></div>
      <div style={ROW}><div style={LABEL}>Require OT approval</div><Toggle on={form.require_ot_approval} onChange={v => setForm(f => ({ ...f, require_ot_approval: v }))} /></div>
      <div style={ROW}><div style={LABEL}>Rest period</div><div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}><input type="number" style={INPUT_NUM} value={form.rest_period_hours} onChange={e => setN('rest_period_hours', e.target.value)} /><span style={{ fontSize: 12, color: palette.g400 }}>hours</span></div></div>
      <div style={ROW}><div style={LABEL}>GPS check-in</div><Toggle on={form.require_gps_checkin} onChange={v => setForm(f => ({ ...f, require_gps_checkin: v }))} /></div>
      <div style={ROW}><div style={LABEL}>Check-in radius</div><div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}><input type="number" style={INPUT_NUM} value={form.checkin_radius_m} onChange={e => setN('checkin_radius_m', e.target.value)} /><span style={{ fontSize: 12, color: palette.g400 }}>meters</span></div></div>
      <div style={ROW}><div style={LABEL}>Grace period</div><div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}><input type="number" style={INPUT_NUM} value={form.grace_minutes} onChange={e => setN('grace_minutes', e.target.value)} /><span style={{ fontSize: 12, color: palette.g400 }}>minutes</span></div></div>
      <div style={ROW}><div style={LABEL}>No-show timeout</div><div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}><input type="number" style={INPUT_NUM} value={form.no_show_timeout} onChange={e => setN('no_show_timeout', e.target.value)} /><span style={{ fontSize: 12, color: palette.g400 }}>minutes</span></div></div>
      <div style={ROW}><div style={LABEL}>Shift swaps</div><select style={SELECT} value={form.shift_swap_mode} onChange={e => setForm(f => ({ ...f, shift_swap_mode: e.target.value }))}><option value="disabled">Disabled</option><option value="requires_approval">Requires approval</option><option value="auto_approve">Auto approve</option></select></div>
      <div style={ROW}><div style={LABEL}>Leave requests</div><select style={SELECT} value={form.leave_request_mode} onChange={e => setForm(f => ({ ...f, leave_request_mode: e.target.value }))}><option value="disabled">Disabled</option><option value="requires_approval">Requires approval</option></select></div>
      <div style={{ ...ROW, borderBottom: 'none' }}><div style={LABEL}>Schedule visibility</div><select style={SELECT} value={form.schedule_visibility} onChange={e => setForm(f => ({ ...f, schedule_visibility: e.target.value }))}><option value="own_shifts_only">Own shifts only</option><option value="team_schedule">Team schedule</option><option value="full_schedule">Full schedule</option></select></div>
      {saveError && (
        <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, fontWeight: 500 }}>{saveError}</div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={() => setEditing(false)} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 500, color: palette.g500, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={save} style={{ padding: '7px 20px', borderRadius: 6, border: 'none', background: palette.accent, color: palette.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Save</button>
      </div>
    </Card>
  );
}
